import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';

import {
    QUERIES_ADMIN,
    ROUTES_ACCOUNT,
    ROUTES_ADMIN,
} from '@constants';

import { useRealState } from 'contexts/RealStateContext';

import { Button, HelperOptions } from 'components/atoms';
import { Table } from 'components/organisms';
import { onErrorMessage, onSuccessMessage } from 'helpers';
import {
    getAdministratorPropertiesTypesIndex,
    removeAdministratorPropertiesTypes,
} from 'services';
import { sweetAlert } from 'utils';

import { PropertiesTypesListWrap } from './styles';
import Layout from '../../Layout';

const PropertiesTypesList = () => {
    const titleMessages = 'Tipos de imóveis!';
    const theme = useTheme();
    const navigate = useNavigate();
    const { getLinkWithSlug } = useRealState();

    const queryClient = useQueryClient();

    const { data, isLoading: isLoadingList } = useQuery(
        QUERIES_ADMIN.PROPERTIES_TYPES.base,
        () => getAdministratorPropertiesTypesIndex()
    );

    const { mutate: removeRegister } =
        useMutation((id: number) => removeAdministratorPropertiesTypes(String(id)), {
            mutationKey: QUERIES_ADMIN.PROPERTIES_TYPES.base,
            onSuccess: () => {
                onSuccessMessage(
                    titleMessages,
                    'Registro removido com sucesso!'
                );

                queryClient.invalidateQueries(QUERIES_ADMIN.PROPERTIES_TYPES.base);
            },
            onError: (err) => {
                onErrorMessage(titleMessages, err);
            },
        });

    function onHandleNavigate(param: string) {
        navigate(param);
    }

    function newRegister() {
        onHandleNavigate(getLinkWithSlug(ROUTES_ADMIN.PROPERTIES_TYPES.FORM.fullPath));
    }

    function editRegister(param: string) {
        onHandleNavigate(
            getLinkWithSlug(`${ROUTES_ADMIN.PROPERTIES_TYPES.FORM.fullPath}/${param}`)
        );
    }

    function removeRegisterRequest(id: number) {
        sweetAlert
            .fire({
                title: 'Você tem certeza?',
                text: 'Se você confirmar este registro será removido e está ação não poderá ser revertida!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: theme.colors.primary,
                cancelButtonColor: theme.colors.danger,
                confirmButtonText: 'Sim, remover registro!',
                cancelButtonText: 'Não, cliquei errado!',
            })
            .then((result) => {
                if (result.isConfirmed) {
                    removeRegister(id);
                }
            });
    }

    return (
        <Layout
            titlePage="Painel Administrativo"
            breadcrumbs={[
                {
                    label: 'Home',
                    href: getLinkWithSlug(ROUTES_ACCOUNT.SELECTED.fullPath),
                },
                {
                    label: 'Tipos dos Imóveis',
                    href: getLinkWithSlug(ROUTES_ADMIN.PROPERTIES_TYPES.fullPath),
                },
            ]}
            title="Tipos dos Imóveis"
            buttonTitle={
                <Button color="nonary" onClick={() => newRegister()}>
                    Novo Registro
                </Button>
            }
        >
            <PropertiesTypesListWrap className="properties-types-template">
                <Table
                    isLoading={isLoadingList}
                    columns={[
                        {
                            dataField: 'id',
                            text: 'ID',
                            sort: true,
                            style: { fontWeight: 700 }
                        },
                        {
                            dataField: 'descricao',
                            text: 'Descrição',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'slug',
                            text: 'Slug',
                            sort: true,
                            headerStyle: { width: '100%' }
                        },
                        {
                            dataField: 'atributos',
                            text: 'Atributos',
                            sort: false,
                            headerStyle: { width: '100%' },
                            formatter: (field) => field?.length || 0
                        },
                        {
                            dataField: 'id',
                            text: 'Opções',
                            formatter: (fieldValue) =>
                                HelperOptions({
                                    edit: true,
                                    editFunction: () =>
                                        editRegister(fieldValue),
                                    removeFunction: () =>
                                        removeRegisterRequest(fieldValue),
                                }),
                            headerStyle: { width: '10%' }
                        },
                    ]}
                    data={data}
                />
            </PropertiesTypesListWrap>
        </Layout>
    );
};

export default PropertiesTypesList;
